import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { Logo } from '../brand';

/**
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH (Emergent Google Auth playbook rule).
 *
 * AuthCallback — post-Google-signin landing surface.
 *
 * The Emergent OAuth broker returns the user to `<origin>/dashboard#session_id=<id>`.
 * This component detects that session_id, exchanges it via
 * POST /api/auth/google-session for our JWT + user, then hard-navigates
 * to /dashboard (via replace, so the fragment is cleaned up from history).
 *
 * If anything goes wrong (expired session, network hiccup) we route the
 * user to /login with a toast — email/password auth still works.
 */
export default function AuthCallback() {
    const nav = useNavigate();
    const { loginWithGoogleSession } = useAuth();
    // useRef (not useState) so React 18 StrictMode's double-mount doesn't
    // fire the exchange twice — session_ids are single-use.
    const hasProcessed = useRef(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (hasProcessed.current) return;
        hasProcessed.current = true;
        const hash = window.location.hash || '';
        // Fragment is "#session_id=abc123&maybeOther=x"
        const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
        const sid = params.get('session_id');
        if (!sid) {
            nav('/login', { replace: true });
            return;
        }
        (async () => {
            try {
                await loginWithGoogleSession(sid);
                // Wipe the session_id from the URL bar before landing on
                // dashboard — otherwise it stays in browser history.
                window.history.replaceState(null, '', '/dashboard');
                nav('/dashboard', { replace: true });
            } catch (e) {
                const msg =
                    e?.response?.data?.detail ||
                    e.message ||
                    'Google sign-in failed. Please try again.';
                setError(msg);
                toast.error(msg);
                setTimeout(() => nav('/login', { replace: true }), 1500);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div
            data-testid="auth-callback"
            className="min-h-screen flex items-center justify-center px-4 dashboard-hero app-noise"
        >
            <div className="text-center max-w-sm">
                <div className="flex items-center justify-center mb-4">
                    <Logo variant="icon" size={48} />
                </div>
                {error ? (
                    <>
                        <div className="text-sm text-destructive mb-2">{error}</div>
                        <div className="text-xs text-muted-foreground">Redirecting to login…</div>
                    </>
                ) : (
                    <>
                        <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                            <Loader2 className="animate-spin" size={16} />
                            Signing you in…
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
